import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import { toast } from 'sonner';
import { 
  Video, Calendar, ExternalLink, 
  MessageSquare, Clock, Shield, ArrowLeft, Star, Brain, Sparkles,
  Search, Filter, X, Download, Copy, FileText, CheckCircle2,
  Stethoscope, Heart, Smile, Apple, Moon, Loader2
} from 'lucide-react';

// Featured Provider - Wheel (White-label telehealth)
const FEATURED_PROVIDER = {
  name: "Wheel Telehealth",
  tagline: "Consult with a healthcare provider about your supplement plan",
  description: "Connect with licensed clinicians who can review your personalized supplement protocol, discuss potential interactions with your medications, and provide professional guidance tailored to your health needs.",
  url: "https://www.wheel.com",
  specialties: ["General Health", "Nutrition"],
  features: ["video", "prescription", "24/7"],
  featureList: [
    "Review your Vitala supplement recommendations with a clinician",
    "Discuss medication interactions and safety",
    "Get personalized dosage guidance",
    "Video or phone consultations",
    "Prescriptions if medically appropriate"
  ]
};

// MS-Specific Telehealth
const MS_TELEHEALTH = {
  name: "MS Specialist Telehealth",
  tagline: "Specialized care for Multiple Sclerosis patients",
  providers: [
    {
      name: "Octave Bioscience",
      description: "MS-specialized telehealth with neurologists who understand MS treatment and supplement interactions",
      url: "https://octave.com",
      specialties: ["MS/Neurology"],
      features: ["video"],
      isFree: false
    },
    {
      name: "MSAA MS Navigator",
      description: "Free support program connecting MS patients with healthcare resources and navigation assistance",
      url: "https://mymsaa.org/msaa-help/msnavigator",
      specialties: ["MS/Neurology"],
      features: ["free"],
      isFree: true
    },
    {
      name: "National MS Society",
      description: "Connect with MS specialists and find neurologists experienced in MS care",
      url: "https://www.nationalmssociety.org/Resources-Support/Find-Doctors-Resources",
      specialties: ["MS/Neurology"],
      features: ["free"],
      isFree: true
    }
  ]
};

const TELEHEALTH_PROVIDERS = [
  {
    name: "Teladoc",
    description: "24/7 access to board-certified doctors via phone or video",
    specialties: ["General Health", "Nutrition", "Mental Health"],
    url: "https://www.teladoc.com",
    rating: 4.8,
    features: ["video", "prescription", "24/7", "mental-health"],
    featureList: ["Video consultations", "Prescription services", "Mental health support"]
  },
  {
    name: "PlushCare",
    description: "Virtual primary care with same-day appointments",
    specialties: ["Primary Care", "Mental Health", "Chronic Care"],
    url: "https://www.plushcare.com",
    rating: 4.8,
    features: ["video", "prescription", "same-day"],
    featureList: ["Same-day appointments", "Ongoing care relationships", "Lab orders"]
  },
  {
    name: "Amwell",
    description: "Connect with doctors and dietitians for medical care anytime",
    specialties: ["General Health", "Mental Health", "Nutrition"],
    url: "https://www.amwell.com",
    rating: 4.6,
    features: ["video", "24/7", "nutrition"],
    featureList: ["Dietitian consultations", "Therapy sessions", "24/7 availability"]
  },
  {
    name: "BetterHelp",
    description: "Online therapy and counseling with licensed therapists",
    specialties: ["Mental Health"],
    url: "https://www.betterhelp.com",
    rating: 4.7,
    features: ["video", "mental-health", "messaging"],
    featureList: ["Licensed therapists", "Unlimited messaging", "Flexible scheduling"]
  },
  {
    name: "Noom",
    description: "Psychology-based approach to weight management and nutrition",
    specialties: ["Nutrition", "Weight Management"],
    url: "https://www.noom.com",
    rating: 4.5,
    features: ["nutrition", "coaching"],
    featureList: ["Personalized plans", "Health coaching", "Behavior change focus"]
  }
];

// Filter options
const SPECIALTY_OPTIONS = [
  { id: "all", label: "All Specialties", icon: Stethoscope },
  { id: "General Health", label: "General Health", icon: Heart },
  { id: "Mental Health", label: "Mental Health", icon: Smile },
  { id: "Nutrition", label: "Nutrition", icon: Apple },
  { id: "MS/Neurology", label: "MS/Neurology", icon: Brain },
];

const FEATURE_OPTIONS = [
  { id: "24/7", label: "24/7 Available" },
  { id: "free", label: "Free Options" },
  { id: "prescription", label: "Prescriptions" },
  { id: "video", label: "Video Visits" },
  { id: "same-day", label: "Same-Day" },
];

export default function TelehealthPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Share results states
  const [latestAssessment, setLatestAssessment] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user) {
      fetchLatestAssessment();
    }
  }, [user]);

  const fetchLatestAssessment = async () => {
    setLoadingAssessment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.assessments && data.assessments.length > 0) {
          setLatestAssessment(data.assessments[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
    } finally {
      setLoadingAssessment(false);
    }
  };

  // Filter providers
  const filterProviders = (providers) => {
    return providers.filter(provider => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = provider.name.toLowerCase().includes(query);
        const descMatch = provider.description.toLowerCase().includes(query);
        if (!nameMatch && !descMatch) return false;
      }
      
      // Specialty filter
      if (selectedSpecialty !== 'all') {
        if (!provider.specialties?.includes(selectedSpecialty)) return false;
      }
      
      // Features filter
      if (selectedFeatures.length > 0) {
        const hasAllFeatures = selectedFeatures.every(f => provider.features?.includes(f));
        if (!hasAllFeatures) return false;
      }
      
      return true;
    });
  };

  const filteredProviders = filterProviders(TELEHEALTH_PROVIDERS);
  const filteredMSProviders = filterProviders(MS_TELEHEALTH.providers);
  const showFeatured = filterProviders([FEATURED_PROVIDER]).length > 0;

  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(f => f !== featureId)
        : [...prev, featureId]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedSpecialty('all');
    setSelectedFeatures([]);
  };

  const hasActiveFilters = searchQuery || selectedSpecialty !== 'all' || selectedFeatures.length > 0;

  // Generate summary for telehealth visit
  const generateVisitSummary = () => {
    if (!latestAssessment) return '';
    
    const { recommendations, medications, health_scores, bmi_data } = latestAssessment;
    
    let summary = `VITALA HEALTH SUMMARY\n`;
    summary += `Generated: ${new Date().toLocaleDateString()}\n\n`;
    
    if (bmi_data) {
      summary += `BMI: ${bmi_data.bmi} (${bmi_data.category})\n\n`;
    }
    
    if (medications && medications.length > 0) {
      summary += `CURRENT MEDICATIONS:\n`;
      medications.forEach(med => {
        summary += `• ${med}\n`;
      });
      summary += `\n`;
    }
    
    if (health_scores) {
      summary += `HEALTH SCORES:\n`;
      Object.entries(health_scores).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          summary += `• ${key.replace(/_/g, ' ')}: ${value}/100\n`;
        }
      });
      summary += `\n`;
    }
    
    if (recommendations && recommendations.length > 0) {
      summary += `SUPPLEMENT RECOMMENDATIONS:\n`;
      recommendations.slice(0, 5).forEach(rec => {
        summary += `• ${rec.name} - ${rec.dosage} (${rec.priority})\n`;
      });
      if (recommendations.length > 5) {
        summary += `  + ${recommendations.length - 5} more...\n`;
      }
    }
    
    summary += `\n---\nFull report available at vitala.me`;
    return summary;
  };

  const copyToClipboard = async () => {
    const summary = generateVisitSummary();
    setCopying(true);
    try {
      await navigator.clipboard.writeText(summary);
      toast.success('Summary copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy');
    } finally {
      setCopying(false);
    }
  };

  const downloadPDF = () => {
    if (latestAssessment) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/assessment/${latestAssessment.id}/pdf`, '_blank');
      toast.success('PDF download started');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="default" linkTo="/" />
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button variant="outline" size="sm">Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 flex-1">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Video className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
            Virtual Healthcare
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Connect with licensed healthcare providers from the comfort of your home. 
            Discuss your supplement protocol, get medical advice, and manage your health online.
          </p>
        </motion.div>

        {/* Share Results Quick Action - Only for logged in users with assessments */}
        {user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-1">
                    Share Your Vitala Results
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {latestAssessment 
                      ? "Quickly share your health summary with your telehealth provider"
                      : "Complete an assessment to share your results with providers"
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2 ml-16 md:ml-0">
                {loadingAssessment ? (
                  <Button disabled variant="outline">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Loading...
                  </Button>
                ) : latestAssessment ? (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={copyToClipboard}
                      disabled={copying}
                      data-testid="copy-summary-btn"
                    >
                      {copying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      Copy Summary
                    </Button>
                    <Button 
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={downloadPDF}
                      data-testid="download-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="btn-primary"
                    onClick={() => navigate('/quiz')}
                  >
                    Take Assessment
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-border p-4 mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search providers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="provider-search-input"
              />
            </div>
            
            {/* Specialty Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
              {SPECIALTY_OPTIONS.map((option) => (
                <Button
                  key={option.id}
                  variant={selectedSpecialty === option.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedSpecialty(option.id)}
                  className={`whitespace-nowrap ${selectedSpecialty === option.id ? 'btn-primary' : ''}`}
                  data-testid={`filter-specialty-${option.id}`}
                >
                  <option.icon className="w-4 h-4 mr-1" />
                  {option.label}
                </Button>
              ))}
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-primary/10' : ''}
            >
              <Filter className="w-4 h-4 mr-1" />
              Filters
              {selectedFeatures.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary text-white text-xs rounded-full">
                  {selectedFeatures.length}
                </span>
              )}
            </Button>
          </div>

          {/* Feature Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 mt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-3">Filter by features:</p>
                  <div className="flex flex-wrap gap-2">
                    {FEATURE_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => toggleFeature(option.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedFeatures.includes(option.id)
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`filter-feature-${option.id}`}
                      >
                        {selectedFeatures.includes(option.id) && (
                          <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        )}
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Active Filters & Clear */}
          {hasActiveFilters && (
            <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {filteredProviders.length + filteredMSProviders.length + (showFeatured ? 1 : 0)} providers
              </p>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear filters
              </Button>
            </div>
          )}
        </motion.div>

        {/* Featured Provider - Wheel */}
        {showFeatured && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-2xl p-8 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase tracking-wide">Recommended</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">{FEATURED_PROVIDER.name}</h2>
            <p className="text-lg text-primary font-medium mb-4">{FEATURED_PROVIDER.tagline}</p>
            <p className="text-muted-foreground mb-6">{FEATURED_PROVIDER.description}</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {FEATURED_PROVIDER.featureList.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  </div>
                  <span className="text-sm text-[#1A1A1A]">{feature}</span>
                </div>
              ))}
            </div>
            
            <a href={FEATURED_PROVIDER.url} target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary">
                <Video className="w-4 h-4 mr-2" />
                Schedule Consultation
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </motion.div>
        )}

        {/* MS-Specific Section */}
        {filteredMSProviders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#1A1A1A]">{MS_TELEHEALTH.name}</h2>
                <p className="text-sm text-muted-foreground">{MS_TELEHEALTH.tagline}</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {filteredMSProviders.map((provider, index) => (
                <motion.div
                  key={provider.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + index * 0.05 }}
                  className="bg-white rounded-xl border border-blue-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-[#1A1A1A]">{provider.name}</h3>
                    {provider.isFree && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        Free
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{provider.description}</p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {provider.specialties.map((specialty) => (
                      <span 
                        key={specialty}
                        className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                  <a href={provider.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="w-full">
                      Visit <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Info Banner - Only show when not logged in */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-primary mt-1" />
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">
                  Share Your Vitala Results
                </h3>
                <p className="text-muted-foreground mb-4">
                  When consulting with a telehealth provider, you can share your Vitala assessment results 
                  using the "Share with Provider" feature on your results page. This helps your doctor 
                  understand your supplement protocol and make informed recommendations.
                </p>
                <Link to="/auth?mode=register&redirect=/telehealth">
                  <Button size="sm" className="btn-primary">
                    Sign Up to Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Other Providers */}
        {filteredProviders.length > 0 && (
          <>
            <h2 className="text-xl font-bold text-[#1A1A1A] mb-6">
              {hasActiveFilters ? 'Matching Providers' : 'Other Telehealth Providers'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProviders.map((provider, index) => (
                <motion.div
                  key={provider.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + index * 0.05 }}
                  className="bg-white rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold text-[#1A1A1A]">{provider.name}</h3>
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm font-medium">{provider.rating}</span>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground text-sm mb-4">{provider.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground mb-2">Specialties</p>
                    <div className="flex flex-wrap gap-1">
                      {provider.specialties.map((specialty) => (
                        <span 
                          key={specialty}
                          className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-xs text-muted-foreground mb-2">Features</p>
                    <ul className="space-y-1">
                      {provider.featureList.map((feature) => (
                        <li key={feature} className="text-sm text-[#1A1A1A] flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <a
                    href={provider.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full btn-primary" data-testid={`telehealth-${provider.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit {provider.name}
                    </Button>
                  </a>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* No Results State */}
        {filteredProviders.length === 0 && filteredMSProviders.length === 0 && !showFeatured && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-white rounded-2xl border border-border"
          >
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No providers found</h3>
            <p className="text-muted-foreground mb-4">Try adjusting your filters or search terms</p>
            <Button variant="outline" onClick={clearFilters}>
              Clear all filters
            </Button>
          </motion.div>
        )}

        {/* Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-white rounded-2xl border border-border p-8"
        >
          <h2 className="text-xl font-semibold text-[#1A1A1A] mb-6">
            Tips for Your Telehealth Visit
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-[#1A1A1A] mb-1">Prepare Your Questions</h3>
                <p className="text-sm text-muted-foreground">
                  Write down your questions about supplements, dosages, and potential interactions before the visit.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-[#1A1A1A] mb-1">Share Your Results</h3>
                <p className="text-sm text-muted-foreground">
                  Use Vitala's share feature to send your assessment to your provider before the appointment.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium text-[#1A1A1A] mb-1">List Your Medications</h3>
                <p className="text-sm text-muted-foreground">
                  Have a complete list of all medications and supplements you're currently taking.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center text-sm text-muted-foreground"
        >
          <p>
            Vitala is not affiliated with any of the telehealth providers listed above. 
            These are independent services. Always consult with a qualified healthcare 
            professional before making changes to your supplement regimen.
          </p>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
